/**
 * Collection load more.
 *
 * Turns the Load more link at the foot of the collection into an append: fetch
 * the next page of the section, add its cards under the ones already on screen,
 * and move the progress bar on. Everything it needs is server-rendered by
 * snippets/collection-load-more.liquid, so with this file blocked or still in
 * flight the link is ordinary paging that works.
 *
 * The block is never rebuilt from numbers held here. Each page is fetched with
 * ?section_id=, which returns the whole section rendered for that page, and the
 * new count, bar width and next-page href are read straight out of it. That
 * keeps one source of truth -- Liquid -- and means the block cannot drift out
 * of step with the grid however many pages get added.
 *
 * theme.js re-renders .collection__dynamic-part wholesale when the shopper
 * sorts, switches layout or changes the page size, taking this block with it,
 * so every listener here is delegated from the document rather than bound to
 * the element.
 */
(function () {
  'use strict';

  var BLOCK = '[data-collection-progress]';
  var TRIGGER = '[data-collection-load-more]';
  var GRID = '.product-list--collection';
  var ITEM = '.product-item';

  function closest(element, selector) {
    // Element.closest is everywhere this theme supports, but the click target
    // can be the label span inside the link, so start from the element itself.
    return element && element.closest ? element.closest(selector) : null;
  }

  /**
   * The grid this block pages. Both live inside the section, and a collection
   * page only ever has one, but scoping to the section keeps it honest if the
   * block is ever rendered somewhere with a second product list on the page.
   */
  function gridFor(block) {
    var scope = closest(block, '[data-section-type="collection"]') || document;
    return scope.querySelector(GRID);
  }

  /**
   * Same URL the link already points at, asked for as a bare section render.
   */
  function sectionUrl(href, sectionId) {
    var url = new URL(href, window.location.origin);
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }
    return url.href;
  }

  /**
   * What the address bar should say once the page is on screen: the link's own
   * URL, minus the section_id that was only ever for the fetch.
   */
  function browserUrl(href) {
    var url = new URL(href, window.location.origin);
    url.searchParams.delete('section_id');
    return url.pathname + url.search + url.hash;
  }

  function setBusy(trigger, busy) {
    var label = trigger.querySelector('.collection-progress__button-label') || trigger;
    var loading = trigger.getAttribute('data-loading-label');

    if (busy) {
      trigger.setAttribute('aria-busy', 'true');
      if (loading) {
        trigger.setAttribute('data-idle-label', label.textContent);
        label.textContent = loading;
      }
    } else {
      trigger.removeAttribute('aria-busy');
      if (trigger.getAttribute('data-idle-label')) {
        label.textContent = trigger.getAttribute('data-idle-label');
        trigger.removeAttribute('data-idle-label');
      }
    }
  }

  /**
   * Copy the freshly rendered block over the one on screen, part by part.
   *
   * Deliberately not a wholesale node swap: the live region has to survive, and
   * a role="status" element inserted and filled in the same frame is skipped by
   * several screen readers. Updating in place leaves the region where it was,
   * already observed, so the new count is spoken.
   */
  function sync(block, fresh) {
    ['data-viewed', 'data-page', 'data-pages'].forEach(function (name) {
      var value = fresh.getAttribute(name);
      if (value !== null) block.setAttribute(name, value);
    });

    var freshCount = fresh.querySelector('[data-collection-progress-count]');
    var count = block.querySelector('[data-collection-progress-count]');
    if (freshCount && count) {
      count.innerHTML = freshCount.innerHTML;
    }

    var freshTrack = fresh.querySelector('[data-collection-progress-track]');
    var track = block.querySelector('[data-collection-progress-track]');
    if (freshTrack && track) {
      track.setAttribute('aria-valuenow', freshTrack.getAttribute('aria-valuenow'));
    }

    var freshFill = fresh.querySelector('[data-collection-progress-fill]');
    var fill = block.querySelector('[data-collection-progress-fill]');
    if (freshFill && fill) {
      fill.style.width = freshFill.style.width;
    }

    var freshTrigger = fresh.querySelector(TRIGGER);
    var trigger = block.querySelector(TRIGGER);

    if (freshTrigger && trigger) {
      trigger.setAttribute('href', freshTrigger.getAttribute('href'));
    } else if (trigger) {
      // Last page: nothing left to load, so the button goes rather than sitting
      // there disabled.
      trigger.parentNode.removeChild(trigger);
      block.classList.add('collection-progress--complete');
    }

    var status = block.querySelector('[data-collection-progress-status]');
    if (status && count) {
      status.textContent = count.textContent.trim();
    }
  }

  /**
   * Say how many products are on screen, counted from the grid.
   *
   * The server can only count the page it rendered, and products under the stock
   * floor (snippets/stock-floor.liquid) draw no card, so the fetched block holds
   * the new page's count rather than the running one. The cards in the grid are
   * the running count, whichever pages got them there.
   */
  function paintViewed(block, grid) {
    var viewed = grid.querySelectorAll(ITEM).length;
    var count = block.querySelector('[data-collection-progress-count]');
    var number = count && count.querySelector('b');

    block.setAttribute('data-viewed', String(viewed));

    if (number) {
      number.textContent = String(viewed);
    }

    var status = block.querySelector('[data-collection-progress-status]');
    if (status && count) {
      status.textContent = count.textContent.trim();
    }
  }

  /**
   * Hand the appended cards to the theme's own machinery.
   *
   * Both of these are what theme.js runs after it replaces the grid on a sort
   * or a filter change; appended cards need the same or they render without
   * review stars and with every colour swatch shown, however narrow the card.
   * The swatch pass has no public entry point, but it is bound to resize, which
   * does.
   */
  function refreshAppended() {
    if (window.SPR && typeof window.SPR.initDomEls === 'function') {
      window.SPR.initDomEls();
      window.SPR.loadBadges();
    }

    window.dispatchEvent(new Event('resize'));
  }

  function loadNext(trigger) {
    var block = closest(trigger, BLOCK);
    var grid = gridFor(block);
    var href = trigger.getAttribute('href');

    if (!block || !grid || !href) return;

    setBusy(trigger, true);

    fetch(sectionUrl(href, block.getAttribute('data-section-id')), {
      credentials: 'same-origin',
      method: 'GET'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = document.createElement('div');
        parsed.innerHTML = html;

        var nextGrid = parsed.querySelector(GRID);
        var nextBlock = parsed.querySelector(BLOCK);

        if (!nextGrid || !nextBlock) throw new Error('next page had no grid');

        var added = nextGrid.querySelectorAll(ITEM);
        var first = added[0] || null;

        // One fragment, one insert: appending the cards one at a time would lay
        // the grid out again for each of the twenty-four.
        var fragment = document.createDocumentFragment();
        Array.prototype.forEach.call(added, function (item) {
          fragment.appendChild(item);
        });
        grid.appendChild(fragment);

        sync(block, nextBlock);
        paintViewed(block, grid);
        refreshAppended();

        // replaceState, not pushState: a shopper who loaded six pages should get
        // back to where they came from in one press of Back, not six. Rewriting
        // the current entry still means a refresh, or a link shared from here,
        // reopens at this point in the collection rather than at the top.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(window.history.state, '', browserUrl(href));
        }

        setBusy(trigger, false);

        // Focus lands on the first card of the batch so keyboard and screen
        // reader users continue from the new products instead of from the top
        // of the grid. preventScroll keeps the viewport where the shopper left
        // it -- the cards are already below the fold they were reading.
        var link = first && first.querySelector('a[href]');
        if (link && document.activeElement === trigger) {
          link.setAttribute('tabindex', '-1');
          try {
            link.focus({ preventScroll: true });
          } catch (e) {
            link.focus();
          }
        }
      })
      .catch(function () {
        // Whatever went wrong -- offline, a 404 on a page that has since been
        // emptied -- the link still points at a real page. Put it back the way
        // it was and let the next click navigate there normally.
        setBusy(trigger, false);
        trigger.removeAttribute('data-collection-load-more');
      });
  }

  document.addEventListener('click', function (event) {
    var trigger = closest(event.target, TRIGGER);

    if (!trigger) return;

    // Let the browser handle anything that was never a plain left click: a
    // middle click or Cmd-click on the link should open the next page in a tab.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (trigger.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    loadNext(trigger);
  });

  /**
   * Marks the block as script-backed, which is what arms the bar's transition.
   * Runs again after theme.js re-renders the collection, since that markup
   * arrives without the class.
   */
  function enhance() {
    Array.prototype.forEach.call(document.querySelectorAll(BLOCK), function (block) {
      block.classList.add('is-enhanced');
    });
  }

  /* ---------------------------------------------------------------------- *
   * The filtered grid
   *
   * Once the shopper touches a filter or a sort, the Smart Product Filter app
   * owns the collection: it rebuilds the grid from its own card template and
   * re-renders the paging from snippets/globo.filter.pagination.liquid. That
   * template can carry the app's total but not the running count, because the
   * app appends to the grid without telling anyone how much is in it, so the
   * sentence arrives with %%VIEWED%% and %%TOTAL%% still in it and gets
   * finished here.
   *
   * Counting the cards is deliberate. The alternative -- page number times page
   * size -- needs the app's page size, which is set in the app's admin rather
   * than the theme, so a merchant changing it there would silently start the
   * theme lying about how far down the collection the shopper is.
   * ---------------------------------------------------------------------- */

  function paintFiltered() {
    var count = document.querySelector('[data-globo-progress]');
    if (!count) return;

    var template = count.getAttribute('data-count-template');
    var total = parseInt(count.getAttribute('data-total'), 10);
    var grid = document.getElementById('gf-products') || document.querySelector(GRID);

    if (!template || !grid || isNaN(total) || total <= 0) return;

    var viewed = Math.min(grid.querySelectorAll(ITEM).length, total);
    var sentence = template
      .split('%%VIEWED%%')
      .join(String(viewed))
      .split('%%TOTAL%%')
      .join(String(total));

    // Every write below is guarded on having something new to say. The observer
    // that calls this watches the same subtree it writes into, so an
    // unconditional write would keep waking itself.
    if (count.innerHTML === sentence) return;
    count.innerHTML = sentence;

    var percent = Math.round((viewed / total) * 100);
    var block = closest(count, '.collection-progress') || count.parentNode;
    var track = block.querySelector('[data-collection-progress-track]');
    var fill = block.querySelector('[data-collection-progress-fill]');

    if (track) track.setAttribute('aria-valuenow', String(percent));
    if (fill) fill.style.width = percent + '%';

    var status = block.querySelector('[data-collection-progress-status]');
    if (status) status.textContent = count.textContent.trim();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }

  // theme.js swaps .collection__dynamic-part for a freshly rendered one on every
  // sort, layout and page-size change; the block inside it is new markup.
  var dynamicPart = document.querySelector('.collection__dynamic-part');
  if (dynamicPart && window.MutationObserver) {
    new MutationObserver(enhance).observe(dynamicPart, { childList: true, subtree: true });
  }

  // The app fires this on window after each render. Its paging is built in a
  // separate pass that can land either side of the event, so the block is also
  // watched for while the app owns the page.
  window.addEventListener('globoFilterRenderCompleted', function () {
    paintFiltered();

    var appGrid = document.getElementById('gf-grid');
    if (!appGrid || !window.MutationObserver || appGrid.hasAttribute('data-progress-watched')) {
      return;
    }

    appGrid.setAttribute('data-progress-watched', '');
    new MutationObserver(paintFiltered).observe(appGrid, { childList: true, subtree: true });
  });
})();
